import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Print as PrintIcon, Close as CloseIcon } from '@mui/icons-material';
import { TransferDetail } from './InventoryTransfer.hooks';
import { colors } from '../../../styles/theme';
import { INV_STATUS_BRANCH_LABEL } from '../../../constants/inventory-status-labels';
import { mapTransferStatus } from '../../common/status';
import {
  buildPrintChromeHtml,
  escapeHtml,
  openPrintWindow,
  PRINT_NO_PRINT_CSS,
  PRINT_POPUP_BLOCKED_MESSAGE,
} from '../../../utils/printWindow';

interface PrintTransferModalProps {
  open: boolean;
  onClose: () => void;
  transferDetail: TransferDetail | null;
}

const PrintTransferModal: React.FC<PrintTransferModalProps> = ({
  open,
  onClose,
  transferDetail,
}) => {
  const handlePrint = () => {
    const printContent = document.getElementById('transfer-content');
    if (!printContent) return;

    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8"/>
    <title>Detalles de Transferencia - ${escapeHtml(transferDetail?.transferCode ?? '')}</title>
    <style>
      body { font-family: 'Courier New', monospace; margin: 0; padding: 20px; background: white; }
      .transfer { max-width: 400px; margin: 0 auto; }
      .text-center { text-align: center; }
      .text-right { text-align: right; }
      .border-top { border-top: 1px dashed #000; margin: 10px 0; padding-top: 10px; }
      .font-bold { font-weight: bold; }
      .text-lg { font-size: 18px; }
      .text-sm { font-size: 12px; }
      .mb-2 { margin-bottom: 8px; }
      .mb-4 { margin-bottom: 16px; }
      .flex { display: flex; justify-content: space-between; }
      .w-full { width: 100%; }
      .status-chip { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: bold; }
      .status-pendiente { background: ${colors.warning.light}20; color: ${colors.warning.dark}; border: 1px solid ${colors.warning.main}; }
      .status-completado { background: ${colors.success.light}20; color: ${colors.success.dark}; border: 1px solid ${colors.success.main}; }
      .status-cancelado { background: ${colors.error.light}20; color: ${colors.error.dark}; border: 1px solid ${colors.error.main}; }
      table { width: 100%; border-collapse: collapse; margin: 10px 0; }
      th, td { padding: 4px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; font-size: 11px; }
      th { font-weight: bold; background: #f9fafb; }
      @media print {
        body { margin: 0; padding: 10px; }
      }
      ${PRINT_NO_PRINT_CSS}
    </style>
  </head>
  <body>
    ${printContent.innerHTML}
    ${buildPrintChromeHtml()}
  </body>
</html>`;

    const printWindow = openPrintWindow({
      html,
      size: 'ticket',
      windowName: 'printTransfer',
    });

    if (!printWindow) {
      window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pendiente':
        return {
          bgcolor: `${colors.warning.light}20`,
          color: colors.warning.dark,
          border: `1px solid ${colors.warning.main}`,
        };
      case 'Completado':
        return {
          bgcolor: `${colors.success.light}20`,
          color: colors.success.dark,
          border: `1px solid ${colors.success.main}`,
        };
      case 'Cancelado':
        return {
          bgcolor: `${colors.error.light}20`,
          color: colors.error.dark,
          border: `1px solid ${colors.error.main}`,
        };
      default:
        return {
          bgcolor: `${colors.warning.light}20`,
          color: colors.warning.dark,
          border: `1px solid ${colors.warning.main}`,
        };
    }
  };

  const getItemStatusColor = (status: string) => {
    switch (status) {
      case INV_STATUS_BRANCH_LABEL:
        return {
          bgcolor: `${colors.success.light}20`,
          color: colors.success.dark,
          border: `1px solid ${colors.success.main}`,
        };
      case 'En Tránsito':
        return {
          bgcolor: `${colors.info.light}20`,
          color: colors.info.dark,
          border: `1px solid ${colors.info.main}`,
        };
      default:
        return {
          bgcolor: `${colors.warning.light}20`,
          color: colors.warning.dark,
          border: `1px solid ${colors.warning.main}`,
        };
    }
  };

  if (!transferDetail) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Detalles de Transferencia</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box id="transfer-content" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              INVENTARIO MANAGER
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sistema de Gestión de Inventarios
              <br />
              Transferencia de piezas
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              DETALLES DE TRANSFERENCIA
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                <strong>Código de Transferencia:</strong>
              </Typography>
              <Typography variant="body2">{transferDetail.transferCode}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                <strong>Ubicación Origen:</strong>
              </Typography>
              <Typography variant="body2">{transferDetail.source}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                <strong>Ubicación Destino:</strong>
              </Typography>
              <Typography variant="body2">{transferDetail.destination}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                <strong>Creado Por:</strong>
              </Typography>
              <Typography variant="body2">{transferDetail.createdBy}</Typography>
            </Box>
            {transferDetail.completedBy && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  <strong>Aceptado por:</strong>
                </Typography>
                <Typography variant="body2">{transferDetail.completedBy}</Typography>
              </Box>
            )}
            {transferDetail.cancelledBy && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  <strong>Cancelado por:</strong>
                </Typography>
                <Typography variant="body2">{transferDetail.cancelledBy}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                <strong>Fecha:</strong>
              </Typography>
              <Typography variant="body2">{transferDetail.date}</Typography>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}
            >
              <Typography variant="body2">
                <strong>Estado:</strong>
              </Typography>
              <Chip
                label={mapTransferStatus(transferDetail.status).label}
                size="small"
                sx={{
                  ...getStatusColor(transferDetail.status),
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  height: 'auto',
                }}
              />
            </Box>
            {transferDetail.notes && (
              <Box sx={{ mb: 1, mt: 2 }}>
                <Typography variant="body2">
                  <strong>Notas / Observaciones:</strong>
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {transferDetail.notes}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
            PRODUCTOS EN TRANSFERENCIA ({transferDetail.items.length})
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ border: 0, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                '& tbody tr:nth-of-type(odd)': { backgroundColor: 'rgba(2,6,23,0.02)' },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{ bgcolor: 'grey.50', '& th': { fontWeight: 700, color: 'text.secondary' } }}
                >
                  <TableCell>Código</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Ubicación</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transferDetail.items.map((item) => (
                  <TableRow key={item.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {item.productCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {item.productName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.productType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.location}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        size="small"
                        sx={{
                          ...getItemStatusColor(item.status),
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          height: 'auto',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Transferencia generada el {new Date().toLocaleString('es-ES')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sistema de Gestión de Inventarios
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handlePrint} variant="contained" startIcon={<PrintIcon />}>
          Imprimir
        </Button>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintTransferModal;
